import React, { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { X, Info, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { cn } from '@/lib/utils';

interface TrainingInstructionBoxProps {
  moduleId: string;
  title: string;
  content: React.ReactNode;
  className?: string;
}

export const TrainingInstructionBox: React.FC<TrainingInstructionBoxProps> = ({
  moduleId,
  title,
  content,
  className,
}) => {
  const { isModuleDismissed, dismissModule, undismissModule } = useUserPreferences();
  const [isOpen, setIsOpen] = useState(true);
  const isDismissed = isModuleDismissed(moduleId);

  if (isDismissed) {
    return null;
  }

  return (
    <Alert className={cn(
      "mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 relative",
      "dark:from-amber-950/30 dark:to-orange-950/30 dark:border-amber-800",
      className
    )}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 flex-1">
            <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 font-semibold text-primary hover:underline text-left">
                {title}
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            </CollapsibleTrigger>
          </div>
          
          <Button 
            variant="ghost" 
            size="sm"
            className="h-7 px-2 text-amber-600 hover:text-amber-800 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/50 shrink-0"
            onClick={() => dismissModule(moduleId)}
          >
            <span className="text-xs mr-1">Got it</span>
            <X className="h-3 w-3" />
          </Button>
        </div>
        
        <CollapsibleContent className="mt-3">
          <AlertDescription className="text-sm text-gray-700 dark:text-gray-300">
            {content}
          </AlertDescription>
        </CollapsibleContent>
      </Collapsible>
    </Alert>
  );
};

// Help button component to re-show instructions
interface TrainingHelpButtonProps {
  moduleId: string;
  className?: string;
}

export const TrainingHelpButton: React.FC<TrainingHelpButtonProps> = ({
  moduleId,
  className,
}) => {
  const { isModuleDismissed, undismissModule } = useUserPreferences();
  const isDismissed = isModuleDismissed(moduleId);

  if (!isDismissed) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn("h-8 w-8 text-muted-foreground hover:text-primary", className)}
      onClick={() => undismissModule(moduleId)}
      title="Show instructions"
    >
      <HelpCircle className="h-4 w-4" />
    </Button>
  );
};

// Pre-configured instruction content for My Training page
export const MyTrainingInstructions: React.FC = () => (
  <TrainingInstructionBox
    moduleId="training-my-training"
    title="Show Coaches Your Dedication 💪"
    content={
      <div className="space-y-3">
        <p>
          Your training activity helps college coaches see your commitment and work ethic — not just your stats.
        </p>
        
        <div className="space-y-2">
          <p className="font-medium">Here's how it works:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Log daily check-ins to build your streak (coaches love consistency!)</li>
            <li>Earn badges that display on your public profile</li>
            <li>Create and share routines to help other athletes</li>
            <li>Choose which routines to feature on your profile for coaches to see</li>
          </ul>
        </div>
        
        <div className="bg-orange-100/50 dark:bg-orange-900/20 rounded-lg p-3 space-y-2">
          <p>
            <span className="font-medium">🔥 Streak Tip:</span> Log something every day — even a quick "Film Study" or "Recovery Day" counts!
          </p>
          <p>
            <span className="font-medium">🏆 Badges:</span> Badges you earn will automatically show on your profile. The longer your streak, the more impressive it looks to recruiters.
          </p>
        </div>
      </div>
    }
  />
);

// Pre-configured instruction content for Community Training page
export const CommunityTrainingInstructions: React.FC = () => (
  <TrainingInstructionBox
    moduleId="training-community"
    title="Train Together, Get Better Together 🤝"
    content={
      <div className="space-y-3">
        <p>
          Welcome to the Training community — see how other athletes are putting in the work.
        </p>
        
        <div className="space-y-2">
          <p className="font-medium">What you can do here:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Browse check-ins and routines from athletes across the platform</li>
            <li>Like routines that inspire you — it helps great content rise to the top</li>
            <li>Follow athletes whose training you want to keep up with</li>
            <li>Share your own routines to help others and build your reputation</li>
            <li>Get ideas for new workouts, nutrition plans, and recovery tips</li>
          </ul>
        </div>
        
        <div className="bg-orange-100/50 dark:bg-orange-900/20 rounded-lg p-3 space-y-2">
          <p>
            <span className="font-medium">💡 Pro Tip:</span> Athletes with the most-liked routines earn special badges. Share what's working for you!
          </p>
          <p>
            <span className="font-medium">👀 Note:</span> Coaches browse this page too — your engagement and contributions show leadership.
          </p>
        </div>
      </div>
    }
  />
);
